import { useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAppStore } from "../../state/useAppStore";
import { formatWhen } from "../../lib/when";
import { roleLabel } from "../../lib/roleLabel";
import { apiGet } from "../../api/client";

/**
 * Context rail — a passive, surface-aware summary.
 *
 * NEW-TM-3: there is no prompt box anywhere in the product. This rail used to
 * carry a chat that POSTed to /xami/chat, which DEL-XAMI removed; the input
 * survived the route it called, so it was both a dead control and the exact
 * affordance the Two Minds design forbids. An agent's output reaches a person
 * through a run, a judgement fork or the Ledger — never a conversation.
 *
 * On a run it becomes that run's background: what the agent is, how the run
 * ended, which rules blocked it, whether a model was involved, and what a
 * person decided about it and when. It reads the same query the run detail
 * page does, so opening the rail costs nothing the page had not already paid.
 */
function surfaceSummary(pathname: string): string {
  if (pathname.includes("/ledger") || pathname.includes("/outcomes")) {
    return "Hours returned, counted from the run records — one entry per run that produced something, valued at a baseline a named person declared. A decision nobody has declared a baseline for reads \"not measured\", never zero.";
  }
  if (pathname.includes("/agent-health")) {
    return "What broke and what a schedule could not do. A run stopped by a rule is not a fault — that is the gate working, and it is in someone's queue with its citation.";
  }
  if (pathname.includes("/autonomy")) {
    return "What each decision type has earned. Autonomy moves one decision at a time, paced by how reversible the action is — and anything irreversible once sent is approved by a person every time.";
  }
  if (pathname.includes("/regulatory")) {
    return "The regulation knowledge base: which instrument was in force on a date, and which rules apply. A lookup, not a chat — every answer carries its citation.";
  }
  if (pathname.match(/\/workforce\/runs\/[^/]+/)) {
    return "One run, in full — what the code computed, the rules applied, whether a model was called, and the narrative after every figure was substituted.";
  }
  if (pathname.includes("/workforce") || pathname.includes("/hands")) {
    return "The agent roster. Each agent serves one role and one task, on a schedule or an event trigger.";
  }
  if (pathname.match(/\/plays\/[^/]+/)) {
    return "A routine's run detail — calculated fields, the rules applied, the drafted output and any judgement fork.";
  }
  if (pathname.includes("/plays")) {
    return "Scheduled routines and their run history.";
  }
  if (pathname.includes("/operator")) {
    return "Decisions waiting on you. Agents interrupt here and nowhere else.";
  }
  if (pathname.includes("/outcomes")) {
    return "Hours returned against declared baselines, with escalation rate and first-pass yield.";
  }
  if (pathname.includes("/settings")) {
    return "Who receives each report, the organisation the agents answer to, how outbound mail signs itself, and how this workspace notifies you. The workspace's own sections are the supervisor's.";
  }
  return "Agents run on their schedules. Anything needing a person's decision arrives in Decisions.";
}

function pageLabel(pathname: string): string {
  if (pathname.includes("/ledger")) return "Efficiency Ledger";
  if (pathname.includes("/agent-health")) return "Runs and schedules";
  if (pathname.includes("/autonomy")) return "Autonomy";
  if (pathname.includes("/templates")) return "Template studio";
  if (pathname.includes("/threads")) return "Threads";
  if (pathname.includes("/regulatory")) return "Regulatory memory";
  if (pathname.match(/\/workforce\/runs\/[^/]+/)) return "Run detail";
  if (pathname.includes("/workforce") || pathname.includes("/hands")) return "Workforce";
  if (pathname.match(/\/plays\/[^/]+/)) return "Run detail";
  if (pathname.includes("/plays")) return "Routines";
  if (pathname.includes("/operator")) return "Decisions";
  if (pathname.includes("/outcomes")) return "Efficiency Ledger";
  if (pathname.includes("/settings")) return "Settings";
  // The dashboard is home, not a department: the label used to say
  // "Department" on the page a person lands on.
  return "Home";
}

/** The slice of a run the rail needs. Same query key as RunDetail. */
interface RunContext {
  run: {
    run_id: string;
    hand_name: string;
    verdict: string;
    created_at: string;
    duration_ms: number;
  };
  gate_pre: { missing?: string[]; pack_version?: string; source?: string };
  llm: { model: string; tokens_in: number; tokens_out: number } | null;
  rules: {
    rule_id: string;
    verdict: string;
    origin: string;
    origin_ref: string | null;
    as_of: string;
    human_summary: string;
  }[];
  resolution: {
    decision: string;
    reason: string;
    resolved_role: string;
    resolved_at: string;
  } | null;
}

function verdictWords(v: string): string {
  switch (v) {
    case "auto_completed":
      return "Completed unattended";
    case "blocked_pre":
      return "Stopped before drafting";
    case "escalated":
      return "Sent to a person";
    default:
      return v;
  }
}

function RunBackground({ workspaceId, runId }: { workspaceId: string; runId: string }) {
  const q = useQuery<RunContext, Error>({
    queryKey: ["hand-run", workspaceId, runId],
    enabled: !!workspaceId && !!runId,
    queryFn: () => apiGet<RunContext>(`/api/workspaces/${workspaceId}/hand-runs/${runId}`),
  });
  if (!q.data) return null;
  const { run, gate_pre, llm, rules, resolution } = q.data;
  const blocking = rules.filter((r) => r.verdict === "block");
  const asOf = rules[0]?.as_of;

  // One .rail-msg per section. .rail-msg is a flex row meant for a single
  // bubble; several bubbles inside one of them render side by side as
  // narrow columns, which is how this first shipped.
  return (
    <div data-testid="rightrail-run">
      <div className="rail-msg">
        <div className="bubble">
          <div style={{ fontWeight: 600 }}>{run.hand_name}</div>
          <div style={{ marginTop: 4 }}>{verdictWords(run.verdict)}</div>
          <div style={{ opacity: 0.7, fontSize: 12, marginTop: 2 }}>
            {formatWhen(run.created_at)} · {(run.duration_ms / 1000).toFixed(1)}s
          </div>
        </div>
      </div>

      <div className="rail-msg">
        <div className="bubble">
          {/* Said either way: a model that was not called is the design working. */}
          {llm ? (
            <div>
              Drafted by <b>{llm.model}</b>, {llm.tokens_in.toLocaleString()} tokens in,{" "}
              {llm.tokens_out.toLocaleString()} out.
            </div>
          ) : (
            <div>
              <b>No model was called.</b> The deterministic checks stopped this run before any
              drafting.
            </div>
          )}
          {gate_pre.pack_version ? (
            <div style={{ opacity: 0.7, fontSize: 12, marginTop: 4 }}>
              Rules from pack {gate_pre.pack_version}
              {asOf ? ` as of ${asOf}` : ""}
              {gate_pre.source === "local_fallback" ? " (offline copy)" : ""}
            </div>
          ) : null}
        </div>
      </div>

      {blocking.length > 0 ? (
        <div className="rail-msg">
          <div className="bubble" data-testid="rightrail-blocking">
            <div style={{ fontWeight: 600 }}>Blocked by</div>
            {blocking.map((r) => (
              <div key={r.rule_id} style={{ marginTop: 4, fontSize: 12 }}>
                <b>{r.rule_id}</b> — {r.human_summary}
                {r.origin_ref ? <div style={{ opacity: 0.7 }}>{r.origin_ref}</div> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {(gate_pre.missing ?? []).length > 0 ? (
        <div className="rail-msg">
          <div className="bubble" data-testid="rightrail-missing">
            <div style={{ fontWeight: 600 }}>Still needed</div>
            {/* The rule's own sentence, not just its code: "PRM-007" tells a
                reviewer nothing about what is being asked of them. */}
            {gate_pre.missing!.map((id) => {
              const r = rules.find((x) => x.rule_id === id);
              return (
                <div key={id} style={{ marginTop: 4, fontSize: 12 }}>
                  <b>{id}</b>
                  {r ? ` — ${r.human_summary}` : ""}
                  {r?.origin_ref ? <div style={{ opacity: 0.7 }}>{r.origin_ref}</div> : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* The human history of this run, or the fact that there is none yet. */}
      {resolution ? (
        <div className="rail-msg">
          <div className="bubble" data-testid="rightrail-decision">
            <div style={{ fontWeight: 600 }}>Decided</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              <b>{resolution.decision.replace(/_/g, " ")}</b> by{" "}
              {roleLabel(resolution.resolved_role)} on {formatWhen(resolution.resolved_at)}
            </div>
            <div style={{ fontSize: 12, marginTop: 4, opacity: 0.85 }}>“{resolution.reason}”</div>
          </div>
        </div>
      ) : run.verdict === "blocked_pre" || run.verdict === "escalated" ? (
        <div className="rail-msg">
          <div className="bubble" data-testid="rightrail-awaiting">
            <div style={{ fontWeight: 600 }}>Awaiting a decision</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              Nobody has acted on this yet. It is in Decisions.
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function RightRail() {
  const collapsed = useAppStore((s) => s.railCollapsed);
  const toggle = useAppStore((s) => s.toggleRail);
  const { pathname } = useLocation();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const runId = pathname.match(/\/workforce\/runs\/([^/]+)/)?.[1] ?? null;

  if (collapsed) {
    return (
      <aside className="rail rail-collapsed" data-testid="rightrail">
        <button
          type="button"
          className="rail-tab"
          onClick={toggle}
          aria-label="Expand context rail"
          style={{ background: "transparent", border: 0, font: "inherit" }}
        >
          Context
        </button>
      </aside>
    );
  }

  return (
    <aside className="rail" data-testid="rightrail">
      <div className="rail-head">
        <div>
          <div className="name">{pageLabel(pathname)}</div>
          <div className="status">
            <span className="pulse" />
            Synthetic demonstration data
          </div>
        </div>
        <div className="right">
          <button
            type="button"
            className="x"
            aria-label="Collapse context rail"
            onClick={toggle}
            style={{ background: "transparent", border: 0 }}
          >
            ×
          </button>
        </div>
      </div>
      <div className="rail-body">
        {runId && workspaceId ? (
          <RunBackground workspaceId={workspaceId} runId={runId} />
        ) : (
          <div className="rail-msg" data-testid="rightrail-summary">
            <div className="bubble">{surfaceSummary(pathname)}</div>
          </div>
        )}
      </div>
    </aside>
  );
}
